// src/pages/offlineSales/components/PurchaseHistoryModal.jsx
//
// Extracted from src/pages/offlineSales/CustomerListPage.jsx (was an inline component there)
// so it can also be used from ReceivablesPage.jsx. Fixes the previously-blank Status column:
// the backend now includes OrderStatus (customer.service.js::getCustomerOrderHistory), so this
// reads o.OrderStatus?.key/label/colorCode instead of the never-populated o.status, using the
// same colorCode-badge pattern as OfflineSalesOrderListPage.jsx. Also surfaces paid/balance.
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { DataTable } from '../../../components/common';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { getCustomerHistory } from '../../../services/api/offlineSales/customers';
import { formatDate } from '../../../utils/dateUtils';
import styles from './PurchaseHistoryModal.module.css';

const Badge = ({ color, label }) => (
  <span className={styles.badge} style={{ backgroundColor: `${color || '#9CA3AF'}20`, borderColor: color || '#9CA3AF', color: color || '#374151' }}>
    {label}
  </span>
);

const PurchaseHistoryModal = ({ customer, onClose }) => {
  const { t, formatCurrency } = useLocalization();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getCustomerHistory(customer.id, { page, limit });
      setOrders(result.orders || []);
      setTotal(result.total || 0);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [customer.id, page]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.historyModal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>{t('offlineSales.purchaseHistory')} — {customer.name}</h3>
          <button onClick={onClose} className={styles.iconBtn}><X size={16} /></button>
        </div>
        <div className={styles.modalBody}>
          {loading ? (
            <div className={styles.loading}><div className={styles.spinner} /></div>
          ) : orders.length === 0 ? (
            <p className={styles.empty}>{t('offlineSales.noPurchaseHistory')}</p>
          ) : (
            <DataTable
              data={orders}
              columns={[
                { key: 'orderNumber', header: t('offlineSales.orderNumberCol'), render: (o) => <Link to={`/offline-sales/orders/${o.id}`}>{o.orderNumber}</Link> },
                { key: 'createdAt', header: t('offlineSales.date'), render: (o) => formatDate(o.createdAt) },
                { key: 'grandTotal', header: t('offlineSales.totalCol'), align: 'right', render: (o) => formatCurrency(o.grandTotal) },
                { key: 'paidAmount', header: t('offlineSales.paidCol'), align: 'right', render: (o) => formatCurrency(o.paidAmount) },
                {
                  key: 'balanceAmount', header: t('offlineSales.balanceCol'), align: 'right',
                  render: (o) => parseFloat(o.balanceAmount) > 0
                    ? <span className={styles.balanceDue}>{formatCurrency(o.balanceAmount)}</span>
                    : formatCurrency(0),
                },
                {
                  key: 'status', header: t('offlineSales.statusCol'),
                  render: (o) => <Badge color={o.OrderStatus?.colorCode} label={t(`offlineSales.status_${o.OrderStatus?.key}`, o.OrderStatus?.label)} />,
                },
              ]}
            />
          )}
          {total > limit && (
            <div className={styles.pagination}>
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className={styles.pageBtn}>‹</button>
              <span>{page} / {Math.ceil(total / limit)}</span>
              <button disabled={page >= Math.ceil(total / limit)} onClick={() => setPage(p => p + 1)} className={styles.pageBtn}>›</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PurchaseHistoryModal;
